import { useState, useCallback, useEffect, useRef } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useDispatch, useSelector } from 'react-redux';
import WidgetDisplay from './WidgetDisplay';
import { saveWidgetLayout, updateMultipleWidgetPositions } from '../../../../app/store/slices/adminSlice';
import { saveUserWidgetLayout, updateMultipleUserWidgetPositions } from '../../../../app/store/slices/userDashboardSlice';
import { toast } from 'react-toastify';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);
const findNextAvailablePosition = (existingLayout, cols = 12, defaultSize = { w: 4, h: 3 }) => {
  if (!existingLayout || existingLayout.length === 0) {
    return { x: 0, y: 0, w: defaultSize.w, h: defaultSize.h };
  }
    const occupiedCells = {};
  let maxY = 0;
  
  existingLayout.forEach(item => {
    const x = parseInt(item.x) || 0;
    const y = parseInt(item.y) || 0;
    const w = parseInt(item.w) || defaultSize.w;
    const h = parseInt(item.h) || defaultSize.h;
    
    for (let i = x; i < x + w; i++) {
      for (let j = y; j < y + h; j++) {
        occupiedCells[`${i},${j}`] = true;
      }
    }
    
    // Track the maximum Y coordinate
    maxY = Math.max(maxY, y + h);
  });
  
  // Try to find a gap in the layout by scanning row by row
  for (let y = 0; y < maxY + 10; y++) {
    for (let x = 0; x < cols - defaultSize.w + 1; x++) {
      let canFit = true;
      
      // Check if this position is free
      for (let i = x; i < x + defaultSize.w; i++) {
        for (let j = y; j < y + defaultSize.h; j++) {
          if (occupiedCells[`${i},${j}`]) {
            canFit = false;
            break;
          }
        }
        if (!canFit) break;
      }
      
      if (canFit) {
        return { x, y, w: defaultSize.w, h: defaultSize.h };
      }
    }
  }
  
  // If no gaps found, place at the bottom
  return { x: 0, y: maxY, w: defaultSize.w, h: defaultSize.h };
};

const ResizableWidgetContainer = ({ 
  widgets = [], 
  context = 'admin', 
  canvasId = null,
  enableResize = true,
  enableDrag = true,
  compact = false
}) => {
  const dispatch = useDispatch();
  
  // Get appropriate loading state based on context
  const layoutSaving = useSelector(state => 
    context === 'admin' 
      ? state.admin?.layoutSaving || false
      : state.userDashboard?.layoutSaving || false
  );
  
  const layoutError = useSelector(state => 
    context === 'admin' 
      ? state.admin?.layoutError
      : state.userDashboard?.layoutError
  );
  
  const lastLayoutSave = useSelector(state => 
    context === 'admin' 
      ? state.admin?.lastLayoutSave
      : state.userDashboard?.lastLayoutSave
  );
  
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [layout, setLayout] = useState([]);
  const layoutRef = useRef(null);

  // Generate layout from widgets with their saved positions
  const generateLayout = useCallback((widgetsArray) => {
    const tempLayout = [];
    const newLayout = [];
    
    widgetsArray.forEach((widget, index) => {
      const savedPosition = widget.position;
      let position;
      
      if (savedPosition?.x !== undefined && savedPosition?.y !== undefined) {
        // Use the saved position if available
        position = {
          x: savedPosition.x,
          y: savedPosition.y,
          w: savedPosition.w || 4,
          h: savedPosition.h || 3
        };
      } else {
        position = findNextAvailablePosition(tempLayout, 12, { w: 4, h: 3 });
      }
      
      const layoutItem = {
        i: widget.id || widget._id || `widget-${index}`,
        x: position.x,
        y: position.y,
        w: position.w || 4,
        h: position.h || 3,
        minW: savedPosition?.minW || 2,
        maxW: savedPosition?.maxW || 12,
        minH: savedPosition?.minH || 2,
        maxH: savedPosition?.maxH || 8,
        static: !enableResize && !enableDrag,
        isResizable: enableResize
      };
      
      newLayout.push(layoutItem);
      tempLayout.push(layoutItem); // temporary layout to calculate next positions
    });
    
    return newLayout;
  }, [enableResize, enableDrag]);

  // Update layout when widgets change
  useEffect(() => {
    const newLayout = generateLayout(widgets);
    setLayout(newLayout);
    layoutRef.current = newLayout;
    // Reset unsaved changes when widgets change (new data loaded)
    setUnsavedChanges(false);
  }, [widgets, generateLayout]);

  // Special effect to detect when a new widget is added
  useEffect(() => {
    if (widgets.length > layout.length && layout.length > 0) {      
      // Get existing widget IDs from current layout
      const existingWidgetIds = new Set(layout.map(item => item.i));
      
      // Find which widgets are new (not in the layout yet)
      const newWidgets = widgets.filter(widget => {
        const widgetId = widget.id || widget._id;
        return !existingWidgetIds.has(widgetId);
      });
      
      if (newWidgets.length > 0) {
        // Create a new layout preserving existing positions
        const updatedLayout = [...layout];
        
        // Add new widgets at available positions
        newWidgets.forEach(widget => {
          const widgetId = widget.id || widget._id;
          const position = findNextAvailablePosition(updatedLayout, 12, { w: 4, h: 3 });
          
          const newLayoutItem = {
            i: widgetId,
            x: position.x,
            y: position.y,
            w: position.w || 4,
            h: position.h || 3,
            minW: 2,
            maxW: 12,
            minH: 2,
            maxH: 8,
            isResizable: enableResize
          };
          
          updatedLayout.push(newLayoutItem);
        });
        
        // Update layout state
        setLayout(updatedLayout);
        layoutRef.current = updatedLayout;
      }
    }
  }, [widgets.length, layout, enableResize]);

  // Handle layout changes
  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
    layoutRef.current = newLayout;
    
    // Only mark as unsaved, don't save automatically
    setUnsavedChanges(true);
  }, []);

  // Manual save function - only way to save changes
  const handleManualSave = useCallback(async () => {
    if (!layoutRef.current || !unsavedChanges) return;
    
    const layoutMap = layoutRef.current.reduce((acc, item) => {
      acc[item.i] = {
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h
      };
      return acc;
    }, {});

    try {
      if (context === 'admin') {
        // Use both thunks for admin
        await dispatch(saveWidgetLayout({
          layoutMap,
          contextType: context,
          canvasId
        })).unwrap();
        
        await dispatch(updateMultipleWidgetPositions({
          layoutMap,
          contextType: context
        })).unwrap();
      } else {
        if (!canvasId) {
          throw new Error('Canvas ID is required for user widget layout');
        }
        
        // Use both thunks for user
        await dispatch(saveUserWidgetLayout({
          layoutMap,
          canvasId
        })).unwrap();
        
        await dispatch(updateMultipleUserWidgetPositions({
          layoutMap,
          canvasId
        })).unwrap();
      }
      
      setUnsavedChanges(false);
      toast.success('Layout saved successfully!');
    } catch (error) {
      console.error('Failed to save layout:', error);
      toast.error('Failed to save layout');
    }
  }, [dispatch, context, canvasId, unsavedChanges]);

  // Reset unsaved changes function
  const handleResetChanges = useCallback(() => {
    const originalLayout = generateLayout(widgets);
    setLayout(originalLayout);
    layoutRef.current = originalLayout;
    setUnsavedChanges(false);
    toast.info('Changes reset to last saved layout', {
      position: 'bottom-right',
      autoClose: 2000
    });
  }, [widgets, generateLayout]);

  const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
  const cols = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };

  const gridProps = {
    className: "relative w-full react-grid-layout",
    layouts: { lg: layout },
    breakpoints,
    cols,
    rowHeight: 60,
    margin: [16, 16],
    containerPadding: [0, 0],
    isDraggable: enableDrag,
    isResizable: enableResize,
    onLayoutChange: handleLayoutChange,
    draggableHandle: '.widget-drag-handle',
    resizeHandles: ['se'],
    useCSSTransforms: true,
    preventCollision: true, 
    compactType: null,   
    resizeHandle: (resizeHandleAxis, ref) => (
      <div 
        className={`react-resizable-handle react-resizable-handle-${resizeHandleAxis}`}
        ref={ref}
        style={{
          position: 'absolute',
          width: '20px',
          height: '20px',
          bottom: '0',
          right: '0',
          cursor: 'nw-resize',
          backgroundColor: '#e5e7eb',
          borderRadius: '4px',
          zIndex: 10
        }}
      />
    )
  };

  if (!widgets || widgets.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg className="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
        <p className="mt-2">No widgets to display</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Layout Status Bar */}
      <div className="flex justify-between items-center mb-4 p-3 bg-gray-50 rounded-lg text-sm">
        <div className="flex items-center space-x-4">
          <span className="text-gray-600 flex items-center">
            Layout Status: 
            {layoutSaving && (
              <>
                <span className="text-blue-600 ml-1">Saving...</span>
                <div className="ml-2 w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </>
            )}
            {unsavedChanges && !layoutSaving && (
              <span className="text-orange-600 ml-1 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Unsaved changes
              </span>
            )}
            {!unsavedChanges && !layoutSaving && (
              <span className="text-green-600 ml-1 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Saved
              </span>
            )}
          </span>
          
          {lastLayoutSave && (
            <span className="text-gray-500 text-xs">
              Last saved: {new Date(lastLayoutSave).toLocaleTimeString()}
            </span>
          )}
          
          {layoutError && (
            <span className="text-red-600 text-xs flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Error: {layoutError}
            </span>
          )}
        </div>
        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          {/* Reset Button */}
          {unsavedChanges && (
            <button
              onClick={handleResetChanges}
              disabled={layoutSaving}
              className="px-3 py-1 bg-btn-secondary text-white text-xs rounded hover:bg-btn-secondary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              title="Reset to last saved layout"
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset
            </button>
          )}
          
          {/* Save Button */}
          <button
            onClick={handleManualSave}
            disabled={layoutSaving || !unsavedChanges}
            className="px-4 py-1 bg-btn-primary text-white text-xs rounded hover:bg-btn-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {layoutSaving ? (
              <>
                <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save Layout
              </>
            )}
          </button>
        </div>
      </div>

      {/* Warning message for unsaved changes */}
      {unsavedChanges && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-orange-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-orange-700 text-sm">
              <strong>You have unsaved layout changes.</strong> Click "Save Layout" to preserve your changes, or "Reset" to discard them.
            </p>
          </div>
        </div>
      )}
      
      <ResponsiveGridLayout {...gridProps}>
        {widgets.map((widget, index) => {
          const widgetId = widget.id || widget._id || `widget-${index}`;
          
          return (
            <div key={widgetId} className="relative bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <PersistentWidget
                widget={widget}
                context={context}
                canvasId={canvasId}
                compact={compact}
                enableResize={enableResize}
                enableDrag={enableDrag}
              />
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
};

//  widget wrapper 
const PersistentWidget = ({ 
  widget, 
  context, 
  canvasId, 
  compact,
  enableResize,
  enableDrag 
}) => {
  return (
    <div className="h-full relative group">
      {/* Drag Handle */}
      {enableDrag && (
        <div className="widget-drag-handle absolute top-2 left-2 cursor-move opacity-0 group-hover:opacity-80 transition-opacity z-20 bg-gray-600 hover:bg-gray-700 rounded p-1">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>
      )}
      
      {/* Enhanced Resize indicator */}
      {enableResize && (
        <div className="absolute bottom-1 right-1 opacity-40 hover:opacity-80 transition-opacity pointer-events-none z-20 bg-gray-400 rounded p-1">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l4-4m0 0l4-4m-4 4v12" />
          </svg>
        </div>
      )}
      
      {/* The actual widget */}
      <div className="h-full w-full">
        <WidgetDisplay 
          widget={widget}
          compact={compact}
          context={context}
          canvasId={canvasId}
          isResizable={enableResize}
        />
      </div>
    </div>
  );
};

export default ResizableWidgetContainer;